import clsx from "clsx";
import { useSwipeable } from "react-swipeable";
import { MdAccountBalance } from "react-icons/md";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { truncateWithEllipsis } from "../../helpers/utils";
import React, {
  memo,
  startTransition,
  useCallback,
  useMemo,
  useState,
} from "react";
import { ALLOW_SWIPE_IN_DESKTOP } from "../../helpers/constants";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import useCurrencies from "src/hooks/useCurrencies";
import useCurrentUser from "src/hooks/useCurrentUser";
import SmartImage from "../SmartImage/SmartImage";
import useUiPreferences from "src/store/useUiPreferences";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import { ICON_SIZES } from "src/constants/icons";

const SWIPE_DELTA = 150;

interface IBankCard {
  account: string;
  bank?: string;
  owner?: string;
  number?: string;
  name?: string;
  selected?: boolean;
  actions?: Array<any>;
  variant?: "blue" | "gray" | "custom";
  actionsTrigger?: "hover" | "normal";
  customStyles?: any;
  className?: string;
  currency: string;
  available?: any;
  onTitleClick?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  disabled?: boolean;
  image?: any;
}

const BankCard: React.FC<IBankCard> = ({
  bank,
  owner,
  number,
  name,
  selected = false,
  actions = [],
  account,
  variant = "blue",
  actionsTrigger = "",
  customStyles = {},
  className,
  currency,
  available = null,
  onTitleClick,
  onSwipeLeft,
  onSwipeRight,
  disabled = false,
  image = null,
}) => {
  const { t } = useTranslation(["common"]);
  const accountsUiPreferences = useUiPreferences((s) => s.accounts);

  const { enabledAccountsList, allUserAccounts } = useCurrentUser();
  const { getCurrencyDataByCode } = useCurrencies();
  const { isMobile } = useMobile();
  const [translateX, setTranslateX] = useState<number>(0);

  const handlers = useSwipeable({
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: true,
    onSwiping: ({ deltaX, dir }) => {
      startTransition(() => {
        if (dir === "Left" || dir === "Right") {
          setTranslateX(deltaX);
        }
      });
    },
    onSwiped: (eventData) => {
      startTransition(() => {
        const { dir, deltaX } = eventData;
        if (Math.abs(deltaX) > SWIPE_DELTA) {
          if (dir === "Left") onSwipeLeft?.();
          if (dir === "Right") onSwipeRight?.();
        }
        setTranslateX(0);
      });
    },
  });

  const getVariantClasses = () => {
    switch (variant) {
      case "gray": {
        return {
          "bg-gray-100": !selected,
          "text-gray-900": !selected,
          "bg-expenser-gray/80 text-white": selected,
          "hover:bg-expenser-gray/80 hover:text-gray-900": !selected,
          "hover:bg-expenser-gray hover:text-white": selected,
        };
      }
      case "blue": {
        return {
          "bg-secondary-100": !selected,
          "text-secondary-900": !selected,
          "bg-primary/80 text-white": selected,
          "hover:bg-primary/80 hover:text-white": !selected,
          "hover:bg-primary hover:text-white": selected,
        };
      }

      case "custom": {
        return {};
      }
    }
  };

  const accountsToShow = useMemo(() => {
    if (accountsUiPreferences.showInactiveAccounts) {
      return allUserAccounts;
    }

    return enabledAccountsList;
  }, [
    accountsUiPreferences.showInactiveAccounts,
    allUserAccounts,
    enabledAccountsList,
  ]);

  const SHARED_STYLES = {
    relative: true,
    flex: true,
    "flex-col": true,
    "rounded md:rounded": true,
    "text-sm": true,
    "p-3": true,
    "gap-6": true,
    "md:gap-6": true,
    "transition-colors": true,
    group: true,
    // "shadow-md": true,
  };

  const currencyData = useMemo(() => {
    return getCurrencyDataByCode({ code: currency });
  }, [currency, getCurrencyDataByCode]);

  const isSwipeable = useMemo(() => {
    return (
      !!onSwipeLeft &&
      !!onSwipeRight &&
      accountsToShow.length &&
      (isMobile ? true : ALLOW_SWIPE_IN_DESKTOP)
    );
  }, [onSwipeLeft, onSwipeRight, accountsToShow, isMobile]);

  return (
    <>
      <div
        className={clsx([
          SHARED_STYLES,
          getVariantClasses(),
          {
            "cursor-grab": isSwipeable,
          },
          className,
        ])}
        style={{
          ...customStyles,
          ...(isSwipeable
            ? {
                touchAction: "pan-y",
                transform: `translateX(${translateX}px)`,
                transition: translateX === 0 ? "transform 0.2s ease" : "none",
              }
            : {}),
        }}
        {...(isSwipeable ? handlers : {})}
      >
        {/* {disabled && (
          <MdWarning
            title={t("pages:account.statuses.inactive")}
            size={ICON_SIZES.XS}
            className={clsx(["absolute", "top-1", "left-1", "text-yellow-400"])}
          />
        )} */}
        <div
          className={clsx([
            "text-right",
            "flex",
            "flex-row",
            "gap-4",
            "justify-between",
            "items-center",
          ])}
        >
          {image ? (
            <div
              className={clsx([
                "inline-flex",
                "h-[48px]",
                "w-[48px]",
                "rounded",
                {
                  "cursor-pointer": !!onTitleClick,
                },
              ])}
              onClick={() => {
                onTitleClick?.();
              }}
            >
              <SmartImage
                src={image}
                alt="Image"
                className={clsx([
                  "inline-flex",
                  "h-[48px]",
                  "w-[48px]",
                  "rounded",
                ])}
              />
            </div>
          ) : (
            <MdAccountBalance
              className={clsx([
                {
                  "cursor-pointer": !!onTitleClick,
                },
              ])}
              size={ICON_SIZES.LG}
              onClick={() => {
                onTitleClick?.();
              }}
            />
          )}
          <div className={clsx(["flex", "flex-col"])}>
            {bank && (
              <p className={clsx(["text-xs", { italic: !bank }])}>
                {bank || t("pages:account.unknown_bank")}
              </p>
            )}
            <p
              className={clsx([
                "text-xs",
                {
                  italic: !currencyData?.name,
                },
              ])}
            >
              {currencyData?.name || t("pages:account.unknown_currency")}
            </p>
          </div>
        </div>

        <div className={clsx(["pr-0", "xl:pr-8"])}>
          <p className={clsx(["truncate", "text-xs", "md:text-sm", "flex"])}>
            <span
              title={onTitleClick ? t("common:tooltips.edit") : ""}
              className={clsx([
                "inline-block",
                "relative",
                "font-normal",
                "truncate",
                "max-w-[70%]",
                "md:max-w-[80%]",
                {
                  "cursor-pointer": !!onTitleClick,
                },
              ])}
              onClick={() => {
                onTitleClick?.();
              }}
            >
              {name
                ? truncateWithEllipsis({
                    text: `${disabled ? "* " : ""}${name}`,
                    maxLen: isMobile ? 30 : 25,
                  })
                : "..."}
            </span>
          </p>
          {available !== null && (
            <div
              className={clsx([
                "flex",
                "flex-row",
                "w-full",
                "justify-start",
                "pointer-events-none",
              ])}
            >
              <CurrencyBadge
                value={available}
                currencySymbol={currencyData?.symbol_native || "?"}
                kind="NONE"
              />
            </div>
          )}
          {number && available === null && (
            <p
              className={clsx([
                "font-normal",
                "truncate",
                "text-xs",
                "md:text-sm",
              ])}
            >
              {number || <span className="text-xs pr-4"></span>}
            </p>
          )}
        </div>
        {actions?.length > 0 && (
          <div
            className={clsx([
              "absolute",
              "bottom-0",
              "right-0",
              "p-2",
              {
                "md:hidden md:group-hover:flex": actionsTrigger === "hover",
              },
            ])}
            onClick={(e: any) => {
              e?.stopPropagation?.();
            }}
          >
            <ButtonGroup gap={1} buttons={actions} />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(BankCard);
